/**
 * The overview's one-line summary under the headline: the library in
 * numbers, from the same facts the cards draw, so it can never disagree
 * with them. Pure, so the phrasing is pinned by tests.
 *
 * @module components/regulation/overviewNutshell
 */
import { overviewFreshnessSlices, overviewNumbers, type OverviewFacts } from "./OverviewSection";
import { FRESHNESS_LABELS } from "./policy/policyFreshness";

export interface NutshellItem {
  readonly value: number;
  readonly label: string;
  /** Draws attention: something is wrong or owed. */
  readonly tone: "neutral" | "bad" | "good";
}

const plural = (count: number, one: string, many: string) => (count === 1 ? one : many);

export function overviewNutshell(facts: OverviewFacts): NutshellItem[] {
  const total = facts.policies.length;
  // A document with an open finding is not counted as up to date.
  const slices = overviewFreshnessSlices(facts);
  const count = (key: string) => slices.find((slice) => slice.key === key)?.count ?? 0;
  const current = count("current");
  const stale = count("out-of-date") + count("review-overdue");
  const unchecked = count("not-governed");
  // Every gap the Findings page lists — regulatory findings plus control
  // amendments and missing documents — the sidebar's Findings count.
  const openFindings = overviewNumbers(facts).findingsWaiting;
  const owned = facts.policies.filter(
    (policy) =>
      (facts.owners.get(policy.id)?.length ?? 0) > 0 ||
      facts.governance.get(policy.id)?.ownerId != null,
  ).length;
  const regimes = facts.overview.frameworks.filter((framework) => framework.enabled).length;

  const items: NutshellItem[] = [
    { value: total, label: plural(total, "policy", "policies"), tone: "neutral" },
    { value: current, label: "up to date", tone: current > 0 ? "good" : "neutral" },
  ];
  if (stale > 0) items.push({ value: stale, label: "out of date", tone: "bad" });
  if (unchecked > 0) {
    items.push({ value: unchecked, label: FRESHNESS_LABELS["not-governed"].toLowerCase(), tone: "neutral" });
  }
  items.push({
    value: openFindings,
    label: `open ${plural(openFindings, "finding", "findings")}`,
    tone: openFindings > 0 ? "bad" : "good",
  });
  items.push({
    value: owned,
    label: `of ${total} with an owner`,
    tone: total > 0 && owned === total ? "good" : "neutral",
  });
  items.push({ value: regimes, label: plural(regimes, "regime adopted", "regimes adopted"), tone: "neutral" });
  return items;
}

/**
 * The same facts as one readable sentence — "21 policies, 14 out of date,
 * 7 not yet checked, 36 open findings, none with an owner and 3 regimes
 * adopted." Zero reads as a word where a number would sound like a tally.
 */
export function overviewNutshellSentence(facts: OverviewFacts): string {
  const items = overviewNutshell(facts);
  const total = facts.policies.length;
  const phrase = (item: NutshellItem): string => {
    if (item.label.startsWith("of ")) {
      return item.value === 0
        ? "none with an owner"
        : item.value === total
          ? "all with an owner"
          : `${item.value} of ${total} with an owner`;
    }
    if (item.value === 0) return `none ${item.label}`.replace("none open findings", "no open findings");
    return `${item.value} ${item.label}`;
  };
  const parts = items.map(phrase);
  if (parts.length <= 1) return `${parts[0] ?? ""}.`;
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}.`;
}
